// src/screens/InventoryScreen.js
// DROP-IN REPLACEMENT
//
// ✅ Matches AccountsScreen placement:
//   - Tabs in the card header (underline indicator)
//   - “Actions row” (InventoryActions + New Product) ABOVE and OUTSIDE the table
//   - Table sits below actions (like AccountActions + AccountsTable)
// ✅ Refresh moved to the tabs row (top-right)
// ✅ Keeps both tabs mounted (prevents blank screens on tab switch)
// ✅ Client-side filtering for group permissions preserved
// ✅ Loads company settings and passes to InventoryTable (pricing visibility control)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import {
  LayoutList,
  ShoppingCart,
  BarChart3,
  AlertCircle,
  Loader2,
  RefreshCw,
  Plus,
  Lock,
  DollarSign,
  Database,
} from "lucide-react";

import { auth, db } from "../firebase";
import useDeveloperFeatureFlags from "../hooks/useDeveloperFeatureFlags";
import { getFeatureAccessState, mergeFeatureFlags } from "../utils/featureAccess";

import InventoryTable from "../components/inventory/InventoryTable";
import InventoryActions from "../components/inventory/InventoryActions";
import InventoryCreateModal from "../components/inventory/InventoryCreateModal";
import InventoryMenuSampleOrderBuilder from "../components/inventory/InventoryMenuSampleOrderBuilder";
import InventoryMetrics from "../components/inventory/InventoryMetrics";
import InventoryPriceChanges from "../components/inventory/InventoryPriceChanges";
import InventoryDuplicateResolver from "../components/inventory/InventoryDuplicateResolver";
import GridsScreen from "./GridsScreen";

const STAFF_ROLES = new Set(["owner", "admin", "super_admin", "enduser", "end_user"]);
const ADMIN_ROLES = new Set(["owner", "admin", "super_admin"]);
const normRole = (v) => String(v || "").trim().toLowerCase();
const normalizeSku = (value) => String(value || "").trim().toLowerCase();
const uniqueStrings = (values) =>
  Array.from(
    new Set((Array.isArray(values) ? values : []).map((value) => String(value || "").trim()).filter(Boolean))
  );

// -----------------------------
// Error Boundary (so blank screens become readable errors)
// -----------------------------
class ScreenErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[InventoryScreen] Tab crashed:", error, info);
    this.setState({ info });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const title = this.props.title || "This section crashed";
    const errMsg = this.state.error?.message || String(this.state.error || "Unknown error");
    const stack = this.state.error?.stack || "";
    const compStack = this.state.info?.componentStack || "";

    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="text-red-800 font-semibold text-sm">{title}</div>
        <div className="text-red-800 text-[12px] mt-1">{errMsg}</div>

        <div className="mt-3 grid grid-cols-1 gap-2">
          {stack ? (
            <pre className="text-[11px] overflow-auto bg-white border border-red-200 rounded-lg p-2 text-slate-700">
              {stack}
            </pre>
          ) : null}

          {compStack ? (
            <pre className="text-[11px] overflow-auto bg-white border border-red-200 rounded-lg p-2 text-slate-700">
              {compStack}
            </pre>
          ) : null}
        </div>

        <div className="text-[11px] text-red-700 mt-2">
          This is the real error that was causing the blank screen. Fix the first line that references your component/file.
        </div>
      </div>
    );
  }
}

export default function InventoryScreen({ companyId: companyIdProp, me: meProp }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(meProp || null);
  const [loadingMe, setLoadingMe] = useState(!meProp);

  // load me from prop (if provided)
  useEffect(() => {
    if (meProp) {
      setMe(meProp);
      setLoadingMe(false);
    }
  }, [meProp]);

  // load me from auth if not provided
  useEffect(() => {
    if (me) return;

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoadingMe(false);
        setMe(null);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setMe({ uid: user.uid, id: snap.id, ...snap.data() });
        else setMe({ uid: user.uid });
      } catch (e) {
        console.error("[InventoryScreen] failed to load user doc:", e);
        setMe({ uid: user.uid });
      } finally {
        setLoadingMe(false);
      }
    });

    return () => unsub();
  }, [me]);

  const cid = companyIdProp || me?.companyId || null;
  const role = useMemo(() => normRole(me?.role), [me?.role]);

  const isStaff = useMemo(() => STAFF_ROLES.has(role), [role]);
  const isAdmin = useMemo(() => ADMIN_ROLES.has(role), [role]);
  const isOwner = useMemo(() => role === "owner", [role]);
  const isCustomer = useMemo(() => role === "customer", [role]);
  const isEndUser = useMemo(() => role === "enduser" || role === "end_user", [role]);

  // Tabs: "table" | "catalog" | "grid" | "metrics" | "priceChanges" | "repairs"
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) return "catalog";
    return "table";
  });
  const openEditItemId = (searchParams.get("itemId") || "").trim() || null;

  const [showCreate, setShowCreate] = useState(false);

  const [rawInventory, setRawInventory] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const developerFeatureFlags = useDeveloperFeatureFlags();

  const [loadingInventory, setLoadingInventory] = useState(true);
  const [error, setError] = useState("");

  // Load company settings (and keep fresh)
  useEffect(() => {
    if (!cid) return;

    const unsub = onSnapshot(
      doc(db, "companies", cid),
      (snap) => {
        setCompanySettings(snap.exists() ? snap.data() : {});
      },
      (err) => {
        console.warn("[InventoryScreen] Company settings listener error:", err);
        setCompanySettings({});
      }
    );

    return () => unsub();
  }, [cid]);

  // ✅ CLIENT-SIDE FILTERING based on user permissions
  const inventory = useMemo(() => {
    if (!rawInventory || rawInventory.length === 0) return [];

    if (isAdmin) return rawInventory;

    if (isCustomer) {
      return rawInventory.filter((item) => item.customerVisible === true);
    }

    if (isEndUser) {
      const userGroups = Array.isArray(me?.groupIds) ? me.groupIds : [];
      if (userGroups.length === 0) {
        console.warn("[InventoryScreen] End user has no groupIds, showing no inventory");
        return [];
      }

      return rawInventory.filter((item) => {
        const itemGroups = Array.isArray(item.groupIds) ? item.groupIds : [];
        if (itemGroups.length === 0) return false;
        return itemGroups.some((g) => userGroups.includes(g));
      });
    }

    return [];
  }, [rawInventory, isAdmin, isCustomer, isEndUser, me?.groupIds]);

  const sortByName = (items) =>
    (items || []).slice().sort((a, b) =>
      String(a?.name || "")
        .toLowerCase()
        .localeCompare(String(b?.name || "").toLowerCase())
    );

  const hasInventoryConflicts = useMemo(() => {
    const currentSkuOwners = new Map();

    inventory.forEach((item) => {
      const skuNorm = normalizeSku(item?.sku);
      if (!skuNorm) return;
      const existing = currentSkuOwners.get(skuNorm) || [];
      existing.push(item.id);
      currentSkuOwners.set(skuNorm, existing);
    });

    const hasDuplicateActiveSku = Array.from(currentSkuOwners.values()).some((owners) => owners.length > 1);
    if (hasDuplicateActiveSku) return true;

    return inventory.some((item) =>
      uniqueStrings(item?.previousSkus).some((previousSku) => {
        const previousSkuNorm = normalizeSku(previousSku);
        if (!previousSkuNorm) return false;
        const currentOwners = currentSkuOwners.get(previousSkuNorm) || [];
        return currentOwners.some((ownerId) => ownerId !== item.id);
      })
    );
  }, [inventory]);

  const loadInventoryOnce = useCallback(async () => {
    if (!cid) {
      setRawInventory([]);
      setLoadingInventory(false);
      return;
    }

    setLoadingInventory(true);
    setError("");

    try {
      const invRef = collection(db, "companies", cid, "inventory");
      const snap = await getDocs(invRef);

      const items = sortByName(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setRawInventory(items);
    } catch (e) {
      console.error("[InventoryScreen] loadInventoryOnce error:", e);
      if (e?.code === "permission-denied") {
        setError("You don't have permission to view this inventory. Contact your administrator.");
      } else {
        setError("Failed to load inventory. Please try again.");
      }
      setRawInventory([]);
    } finally {
      setLoadingInventory(false);
    }
  }, [cid]);

  const loadSampleOrders = useCallback(async () => {
    if (!cid) return;
    if (!isStaff && !isAdmin) return;

    try {
      const ref = collection(db, "companies", cid, "sampleOrders");
      const snap = await getDocs(ref);
      const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSampleOrders(orders);
    } catch (e) {
      if (e?.code !== "permission-denied") {
        console.warn("[InventoryScreen] loadSampleOrders blocked/failed (non-fatal):", e);
      }
      setSampleOrders([]);
    }
  }, [cid, isStaff, isAdmin]);

  // Realtime listener for inventory
  useEffect(() => {
    if (!cid) return;

    const invRef = collection(db, "companies", cid, "inventory");
    setLoadingInventory(true);

    const unsub = onSnapshot(
      invRef,
      (snap) => {
        const items = sortByName(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRawInventory(items);
        setError("");
        setLoadingInventory(false);
      },
      (err) => {
        console.error("[InventoryScreen] realtime listener error:", err);
        if (err?.code === "permission-denied") {
          setError("Permission denied. Your access may have changed.");
        } else {
          setError("Failed to load inventory updates.");
        }
        setLoadingInventory(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  // Initial loads + refreshable fetch
  useEffect(() => {
    if (!cid) return;
    loadInventoryOnce();
    loadSampleOrders();
  }, [cid, loadInventoryOnce, loadSampleOrders]);

  const getProductSampleCount = useCallback(
    (productId) => {
      const orders = Array.isArray(sampleOrders) ? sampleOrders : [];
      return orders.filter((order) => {
        const items = order.items || [];
        return items.some((it) => it.productId === productId);
      }).length;
    },
    [sampleOrders]
  );

  const handleRefresh = useCallback(() => {
    loadInventoryOnce();
    loadSampleOrders();
  }, [loadInventoryOnce, loadSampleOrders]);

  const handleEditInventoryFields = useCallback(() => {
    navigate("/admin?tab=inventoryFields");
  }, [navigate]);

  const clearEditItemParam = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("itemId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openInventoryItem = useCallback(
    (itemId) => {
      if (!itemId) return;
      setActiveTab("table");
      const next = new URLSearchParams(searchParams);
      next.set("itemId", itemId);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (openEditItemId) setActiveTab("table");
  }, [openEditItemId]);

  const inventoryMetricsAccess = useMemo(
    () =>
      getFeatureAccessState({
        featureKey: "inventoryMetrics",
        featureFlags: mergeFeatureFlags(companySettings?.featureFlags || {}, developerFeatureFlags || {}),
        userEmail: me?.email || auth.currentUser?.email || "",
        planType: companySettings?.planType || me?.planType || null,
        subscriptionActive:
          typeof companySettings?.subscriptionActive === "boolean"
            ? companySettings.subscriptionActive
            : me?.subscriptionActive === true,
        stripeSubscriptionId: companySettings?.stripeSubscriptionId || "",
        seatLimits: companySettings?.seatLimits || null,
      }),
    [
      companySettings?.featureFlags,
      companySettings?.planType,
      companySettings?.subscriptionActive,
      companySettings?.stripeSubscriptionId,
      companySettings?.seatLimits,
      developerFeatureFlags,
      me?.email,
      me?.planType,
      me?.subscriptionActive,
    ]
  );
  const inventoryMetricsHiddenEntirely = inventoryMetricsAccess.reason === "hidden_entirely";

  useEffect(() => {
    if (!inventoryMetricsAccess.allowed && activeTab === "metrics") {
      setActiveTab("table");
    }
  }, [inventoryMetricsAccess.allowed, activeTab]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768 && activeTab === "grid") {
      setActiveTab("table");
      return;
    }
    if (isEndUser && activeTab === "grid") {
      setActiveTab("table");
    }
  }, [activeTab, isEndUser]);

  useEffect(() => {
    if (!isOwner && activeTab === "priceChanges") {
      setActiveTab("table");
    }
    if ((!isOwner || !hasInventoryConflicts) && activeTab === "repairs") {
      setActiveTab("table");
    }
  }, [hasInventoryConflicts, isOwner, activeTab]);

  const gridDisabled = isEndUser;
  const metricsDisabled = !isAdmin || !inventoryMetricsAccess.allowed;
  const priceChangesDisabled = !isOwner;
  const repairsDisabled = !isOwner || !hasInventoryConflicts;

  // -------- Guards / loading states --------
  if (loadingMe) {
    return (
      <div className="p-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
          <div className="text-sm text-slate-700">Loading user…</div>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="p-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
          Could not load user. Are you signed in?
        </div>
      </div>
    );
  }

  if (!cid) {
    return (
      <div className="p-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
          Missing <code>companyId</code>.
        </div>
      </div>
    );
  }

  if (!isStaff && !isCustomer && !isAdmin) {
    return (
      <div className="p-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
          Your role does not have access to inventory.
        </div>
      </div>
    );
  }

  // -------- UI helpers --------
  const mobileActiveTab = activeTab === "table" ? "list" : activeTab === "catalog" ? "catalog" : "catalog";

  const TabButton = ({ id, Icon, label, disabled, lockedMessage = "", showLock = false }) => {
    const active = activeTab === id;
    return (
      <button
        type="button"
        onClick={() => !disabled && setActiveTab(id)}
        disabled={disabled}
        className={`flex items-center gap-2 px-6 py-4 font-medium transition-all relative ${
          disabled
            ? "text-slate-300 cursor-not-allowed"
            : active
            ? "text-blue-600 bg-blue-50"
            : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
        }`}
        title={disabled && lockedMessage ? lockedMessage : ""}
      >
        <Icon className="w-5 h-5" />
        <span>{label}</span>
        {showLock ? <Lock className="w-3.5 h-3.5 opacity-70" /> : null}
        {active && !disabled ? <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" /> : null}
      </button>
    );
  };

  const catalogDisabled = false;

  const topRightLabel = isAdmin ? "Admin view" : isCustomer ? "Customer view" : "Group-based view";

  // Action bar styles (match AccountActions sizing)
  const outlineBtn =
    "inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-[13px] font-medium text-slate-700 whitespace-nowrap";
  const outlineBtnDisabled = "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="w-full max-w-none mx-0 px-0 py-1">
      <div className="bg-white overflow-visible md:rounded-2xl md:shadow-lg md:border md:border-slate-200">
        <div className="border-b border-slate-200 px-4 py-3 md:hidden">
          <nav className="inline-flex w-full rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
            <div className="grid w-full grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setActiveTab("catalog")}
                className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-medium transition-all ${
                  mobileActiveTab === "catalog"
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <ShoppingCart className="w-4 h-4 shrink-0" />
                <span>Catalog</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("table")}
                className={`flex items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-medium transition-all ${
                  mobileActiveTab === "list"
                    ? "bg-blue-50 text-blue-700 shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <LayoutList className="w-4 h-4 shrink-0" />
                <span>List</span>
              </button>
            </div>
          </nav>
        </div>

        {/* Tabs row (matches AccountsScreen placement + top-right tools) */}
        <div className="hidden md:block border-b border-slate-200">
          <div className="flex items-stretch">
            <nav className="flex text-sm">
              <TabButton id="table" Icon={LayoutList} label="Table" />
              <TabButton id="catalog" Icon={ShoppingCart} label="Catalog / Menu" disabled={catalogDisabled} />
              <TabButton id="grid" Icon={Database} label="Grid" disabled={gridDisabled} lockedMessage="Grid is unavailable for end users" />
              {inventoryMetricsHiddenEntirely ? null : (
                <TabButton id="metrics" Icon={BarChart3} label="Metrics" disabled={metricsDisabled} showLock={!inventoryMetricsAccess.allowed} lockedMessage={inventoryMetricsAccess.lockMessage || "Metrics locked"} />
              )}
              <TabButton id="priceChanges" Icon={DollarSign} label="Price Changes" disabled={priceChangesDisabled} showLock={!isOwner} lockedMessage="Price Changes are owner-only" />
              {hasInventoryConflicts ? (
                <TabButton id="repairs" Icon={AlertCircle} label="Resolve Duplicates" disabled={repairsDisabled} showLock={!isOwner} lockedMessage="Resolve Duplicates is owner only" />
              ) : null}
            </nav>

            {/* Top-right: role label + refresh (matches your request) */}
            <div className="ml-auto flex items-center gap-2 px-4">
              <div className="text-[11px] text-slate-500 whitespace-nowrap">{topRightLabel}</div>

              <button
                type="button"
                onClick={handleRefresh}
                className={`${outlineBtn} ${outlineBtnDisabled}`}
                title="Refresh inventory"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 md:p-6">
          {/* Error banner (global for screen) */}
          {error ? (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="text-sm text-red-800 flex-1">{error}</div>
              <button
                type="button"
                onClick={() => setError("")}
                className="text-sm font-medium text-red-700 hover:text-red-900"
              >
                Dismiss
              </button>
            </div>
          ) : null}

          {/* Loading hint */}
          {loadingInventory ? (
            <div className="mb-3 flex items-center gap-3 text-slate-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Loading inventory…</span>
            </div>
          ) : null}

          {/* Keep all tabs mounted; toggle visibility like AccountsScreen */}
          <div className={activeTab === "table" ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Table tab crashed">
              <div className="min-h-[50vh]">
                {/* TABLE */}
                <div className="mt-0 md:mt-3">
<InventoryTable
  inventory={inventory}
  companyId={cid}
  onInventoryUpdate={handleRefresh}
  getProductSampleCount={getProductSampleCount}
  userRole={me?.role}
  companySettings={companySettings}
  currentUserId={me?.uid || me?.id || null}
  openEditItemId={openEditItemId}
  onCloseEditItem={clearEditItemParam}
  onEditColumns={isAdmin ? handleEditInventoryFields : null}
  headerActions={
    isAdmin ? (
      <>
        <InventoryActions companyId={cid} onInventoryUpdate={handleRefresh} sampleOrders={sampleOrders} />
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="ml-1 inline-flex items-center gap-2 h-10 px-3.5 rounded-xl bg-gradient-to-r from-sky-600 via-blue-600 to-cyan-500 text-[13px] font-semibold text-white whitespace-nowrap shadow-sm hover:from-sky-700 hover:via-blue-700 hover:to-cyan-600"
          title="Add a new item"
        >
          <Plus className="w-4 h-4" />
          <span>Add Item</span>
        </button>
      </>
    ) : null
  }
/>

                </div>
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "catalog" ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Catalog tab crashed">
              <div className="min-h-[50vh]">
                <div className="hidden md:block text-xs text-slate-500 mb-3">Build a sample order from the menu catalog.</div>

                <div className="p-0">
                  <InventoryMenuSampleOrderBuilder
                    companyId={cid}
                    currentUserId={me?.uid || auth.currentUser?.uid || null}
                    inventoryItems={inventory}
                    onInventoryUpdate={handleRefresh}
                    goToOrderPath="/sample-orders?view=create"
                  />
                </div>
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "metrics" && inventoryMetricsAccess.allowed ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Metrics tab crashed">
              <div className="min-h-[50vh]">
                {metricsDisabled ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
                    Metrics are admin-only.
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-slate-500 mb-3">Performance and stock insights (admin only).</div>
                    <InventoryMetrics
                      inventory={inventory}
                      sampleOrders={sampleOrders}
                      getProductSampleCount={getProductSampleCount}
                    />
                  </>
                )}
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "grid" ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Grid tab crashed">
              <div className="min-h-[50vh]">
                <GridsScreen forcedTab="inventory" showTabNav={false} embedded onInventorySaved={handleRefresh} inventoryItems={inventory} />
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "priceChanges" && isOwner ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Price Changes tab crashed">
              <div className="min-h-[50vh]">
                {priceChangesDisabled ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
                    Price Changes are owner-only.
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-slate-500 mb-3">Owner-only pricing history across your inventory.</div>
                    <InventoryPriceChanges
                      companyId={cid}
                      inventory={inventory}
                      sampleOrders={sampleOrders}
                    />
                  </>
                )}
              </div>
            </ScreenErrorBoundary>
          </div>

          <div className={activeTab === "repairs" && isOwner && hasInventoryConflicts ? "block" : "hidden"}>
            <ScreenErrorBoundary title="Inventory Resolve Duplicates tab crashed">
              <div className="min-h-[50vh]">
                {repairsDisabled ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
                    Resolve Duplicates is owner-only and only appears when conflicts exist.
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-slate-500 mb-3">
                      Separate repair tool for duplicate SKUs and SKU-history collisions.
                    </div>
                    <InventoryDuplicateResolver inventory={inventory} onOpenItem={openInventoryItem} />
                  </>
                )}
              </div>
            </ScreenErrorBoundary>
          </div>
        </div>
      </div>

      {/* Create modal stays outside card like before */}
      <InventoryCreateModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={cid}
        onCreated={() => {
          setShowCreate(false);
          setActiveTab("table");
          handleRefresh();
        }}
      />
    </div>
  );
}
